import { supabase } from "./supabase";
import type { Resposta } from "./adminCatalogo";

/**
 * O CONTROLE DE PRODUÇÃO DE CONTEÚDO (000313 — o G2 do Plano de Melhorias da
 * Equipe de Produtos, e a decisão 3 de Victor: "produção de conteúdo mora no
 * hub").
 *
 * ZERO regra aqui. O que é PRODUZIDA (aprovada ou publicada), o que é ENTREGUE
 * (da revisão em diante), o valor investido, a previsão de término e as
 * atrasadas saem prontos do banco, em `resumo`. A tela não soma nem compara
 * datas: número que ela mostra é número que veio.
 */

export type StatusDaProducao = "planejada" | "em_producao" | "em_revisao" | "ajustes" | "aprovada" | "publicada";
export type TipoDaProducao = "gravada" | "ao_vivo" | "material";

/**
 * O que a Central de Professores sabe de UMA aula (000322): o recibo da última
 * leva e o estado que ela devolveu. Só existe para a aula GRAVADA, com
 * professor, ENTREGUE — ou para a que já foi e deixou de ir. O valor é o de lá:
 * duração × valor-hora, com a origem de cada um.
 */
export type CentralDaAula = {
  vai: boolean;
  /** Nulo: nunca chegou lá. */
  estado: "na_central" | "retirada" | null;
  falta_levar: boolean;
  valor: number | null;
  valor_origem: "calculado" | "manual" | "padrao" | null;
  valor_hora: number | null;
  valor_hora_origem: "producao" | "contrato" | "cadastro" | "nenhum" | null;
  status_financeiro: "nao_liberado" | "aguardando_liberacao" | "liberado_pagamento" | "pago" | null;
  /** O que mudou aqui depois de a Central conferir, e por isso não foi aplicado lá. */
  pendencia: { campos?: string[]; motivo?: string } | null;
  erro: string | null;
  levada_em: string | null;
  sincronizada_em: string | null;
};

export type ItemDeProducao = {
  id: string;
  titulo: string;
  tipo: TipoDaProducao;
  disciplina_id: string | null;
  disciplina: string | null;
  docente_id: string | null;
  professor: string | null;
  duracao_min: number | null;
  /** `date`, AAAA-MM-DD — sem `new Date`, que a poria um dia antes. */
  prazo: string | null;
  status: StatusDaProducao;
  status_rotulo: string;
  revisao_nota: string | null;
  valor: number | null;
  observacao: string | null;
  produzida: boolean;
  atrasada: boolean;
  /** Da revisão em diante (000322): o professor entregou. */
  entregue: boolean;
  /** O dia da entrega, AAAA-MM-DD — decide o mês do pagamento na Central. */
  entregue_em: string | null;
  /** O valor que CONTA: o da Central quando ela o tem (calculado ou digitado lá), senão o daqui. */
  valor_efetivo: number | null;
  valor_da_central: boolean;
  central: CentralDaAula | null;
  criado_por: string;
  atualizado_em: string;
};

export type ProducaoDoProduto = {
  ok: true;
  hoje: string;
  resumo: {
    total: number;
    produzidas: number;
    faltam: number;
    em_revisao: number;
    ajustes: number;
    atrasadas: number;
    faltam_sem_prazo: number;
    previsao_termino: string | null;
    valor_investido: number;
    /** Quanto do investido veio da Central (000322). */
    investido_da_central: number;
    valor_previsto: number;
    sem_valor: number;
    central: { vao: number; levadas: number; faltam: number; com_erro: number; com_pendencia: number };
  };
  itens: ItemDeProducao[];
  /**
   * As aulas que SAÍRAM do controle e a Central ainda guarda (000322) — a que já
   * foi conferida ou paga lá fica, marcada, para a equipe de lá resolver.
   */
  fora_do_controle: Array<{
    item_id: string;
    titulo: string | null;
    estado: "na_central" | "retirada" | null;
    valor: number | null;
    status_financeiro: CentralDaAula["status_financeiro"];
    erro: string | null;
    falta_retirar: boolean;
  }>;
  /**
   * Os estados NA ORDEM do fluxo e os tipos, com o rótulo do banco (000315): o
   * seletor sai daqui, e não de uma segunda lista escrita no front — um estado
   * renomeado lá apareceria com o nome velho na hora de escolher.
   */
  estados: Array<{ valor: StatusDaProducao; rotulo: string }>;
  tipos: Array<{ valor: TipoDaProducao; rotulo: string }>;
  disciplinas: Array<{ id: string; nome: string }>;
  professores: Array<{ id: string; nome: string }>;
};

export async function lerProducaoDoProduto(curso: string): Promise<ProducaoDoProduto | (Resposta & { ok: false })> {
  const { data, error } = await supabase.rpc("cat_admin_producao_do_produto", { p_curso_slug: curso });
  if (error) return { ok: false, detalhe: error.message };
  return data as ProducaoDoProduto | (Resposta & { ok: false });
}

/** Os campos que o salvar aceita. Na edição, só as chaves PRESENTES mudam, e `null` limpa. */
export type CamposDaAula = Partial<{
  titulo: string;
  tipo: TipoDaProducao;
  disciplina_id: string | null;
  docente_id: string | null;
  duracao_min: number | null;
  prazo: string | null;
  valor: number | null;
  observacao: string | null;
  /** Só na aula entregue, nunca no futuro, nunca vazia — quem recusa é o banco. */
  entregue_em: string;
}>;

export async function salvarAula(id: string | null, curso: string, campos: CamposDaAula): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_salvar_item_producao", {
    p_id: id,
    p_curso_slug: curso,
    p_campos: campos,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/** Muda o estado. "Ajustes pedidos" exige a nota — quem recusa sem ela é o banco. */
export async function mudarEstado(id: string, status: StatusDaProducao, nota: string | null): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_mudar_status_producao", { p_id: id, p_status: status, p_nota: nota });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/** Tira a aula do controle. Pede MOTIVO, e a linha inteira fica no histórico. */
export async function tirarAula(id: string, motivo: string): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cat_admin_remover_item_producao", { p_id: id, p_motivo: motivo });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/**
 * Leva agora à Central as aulas gravadas entregues (000322) — a mesma leva que
 * o cron da Central dispara de 15 em 15 minutos, pela porta
 * `professores-da-central`. Quem autoriza é a porta (a aba Catálogo, com
 * edição); a frase conta o que foi, e concorda com o número.
 */
export async function levarProducaoAgora(): Promise<Resposta & { gravadas?: number; com_erro?: number }> {
  const { data, error } = await supabase.functions.invoke("professores-da-central", {
    body: { acao: "levar_producao" },
  });
  const corpo = (data ?? {}) as Record<string, unknown>;
  if (error) {
    let doCorpo: Record<string, unknown> = corpo;
    // Na resposta não-2xx o SDK guarda o corpo em `error.context`.
    const contexto = (error as { context?: Response }).context;
    if (contexto && typeof contexto.json === "function") {
      try {
        doCorpo = (await contexto.json()) as Record<string, unknown>;
      } catch {
        /* sem corpo legível */
      }
    }
    return {
      ok: false,
      detalhe:
        typeof doCorpo.detalhe === "string"
          ? doCorpo.detalhe
          : typeof doCorpo.erro === "string"
            ? doCorpo.erro
            : error.message,
    };
  }
  const n = (k: string) => (typeof corpo[k] === "number" ? (corpo[k] as number) : 0);
  const gravadas = n("gravadas");
  const retiradas = n("retiradas");
  const comErro = n("com_erro");
  const comPendencia = n("com_pendencia");
  const partes: string[] = [];
  if (gravadas) partes.push(gravadas === 1 ? "1 aula levada à Central" : `${gravadas} aulas levadas à Central`);
  if (retiradas) partes.push(retiradas === 1 ? "1 retirada de lá" : `${retiradas} retiradas de lá`);
  if (comErro) partes.push(comErro === 1 ? "1 não chegou, e o motivo fica na linha dela" : `${comErro} não chegaram, e o motivo fica na linha de cada uma`);
  if (comPendencia) {
    partes.push(comPendencia === 1
      ? "1 já estava conferida lá, e a mudança ficou como pendência"
      : `${comPendencia} já estavam conferidas lá, e as mudanças ficaram como pendência`);
  }
  return {
    ok: true, gravadas, com_erro: comErro,
    detalhe: partes.length
      ? `${partes.join("; ")}.`
      : "Nada a levar: as aulas gravadas entregues já estão na Central, e os valores daqui foram atualizados com os de lá.",
  };
}
